import { DateTime, Duration } from "luxon";

import { formatLocalDate } from "@/utils/dateFormat";
import { convertCamelCaseToCapitalize, convertSnakeCaseToNoCaseCapitalize } from "@/utils/str";

const HOUR = Duration.fromObject({ hours: 1 });
const DAY = Duration.fromObject({ days: 1 });
const WEEK = Duration.fromObject({ weeks: 1 });

/** Machine names whose capitalized form is not the product wording. */
const STATUS_DISPLAY_OVERRIDES: Record<string, string> = {
  sleeperBed: "Sleeper Berth",
};

/** Dispatcher-facing label for a duty or driving status machine name; empty when unknown. */
export function formatStatusLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  return STATUS_DISPLAY_OVERRIDES[raw] ?? convertCamelCaseToCapitalize(convertSnakeCaseToNoCaseCapitalize(raw));
}

/** Dispatcher-facing label for a `calculatedAvailability` value; empty when unknown. */
export function formatAvailabilityLabel(raw: string | null | undefined): string {
  switch (raw) {
    case "present":
      return "Available";
    case "notPresent":
      return "Unavailable";
    case "unknown":
      return "Unknown";
    default:
      return "";
  }
}

/** The device volume as the percent the driver sees; empty when unknown. */
export function formatVolume(volume: number | null | undefined): string {
  return typeof volume === "number" ? `${Math.round(volume * 100)}%` : "";
}

/** "Yes"/"No" for a projected boolean field. */
export function formatYesNo(value: string | boolean | null | undefined): string {
  return value === true || value === "true" ? "Yes" : "No";
}

/**
 * A message time as the table shows it: the clock time when it is today, otherwise the date and clock time. Absolute
 * rather than relative so a row never goes stale between data refreshes.
 */
export function formatItemTime(iso: string | null | undefined): { short: string; full: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return {
    short: isToday(date) ? formatLocalDate(date, "HH:mm") : formatLocalDate(date, "MMM d, HH:mm"),
    full: formatLocalDate(date, "EEE, MMM d yyyy 'at' HH:mm:ss"),
  };
}

/** Whether `date` falls on the viewer's calendar today. */
function isToday(date: Date): boolean {
  return DateTime.fromJSDate(date).hasSame(DateTime.now(), "day");
}

/** Whole minutes from `date` to `now`, truncated toward zero; negative while `date` is still ahead. */
function minutesBetween(now: number, date: Date): number {
  return Math.trunc(DateTime.fromMillis(now).diff(DateTime.fromJSDate(date)).as("minutes"));
}

/**
 * Words for how far an instant sits from now: "42 min", "3 hr", "2 days", or null once a week separates them.
 */
function ageWords(minutes: number): string | null {
  const age = Duration.fromObject({ minutes });
  if (age.toMillis() < HOUR.toMillis()) return `${minutes} min`;
  if (age.toMillis() < DAY.toMillis()) return `${Math.floor(age.as("hours"))} hr`;
  if (age.toMillis() < WEEK.toMillis()) {
    const days = Math.floor(age.as("days"));
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  return null;
}

/**
 * A timestamp as the table's Time column shows it: the local clock time, the age in words, and the full form for a
 * tooltip. The clock is `HH:mm:ss` today and the date with `HH:mm` before that; the age reads "just now", "42 min ago",
 * "3 hr ago", "2 days ago", then nothing once the clock already names the day. An instant still ahead, such as a
 * planned time of availability, reads "in 42 min" on the same scale. `now` is passed in so the table re-renders every
 * minute from one clock.
 */
export function formatClockAndAge(
  iso: string | null | undefined,
  now: number,
): { clock: string; age: string | null; full: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const elapsed = minutesBetween(now, date);
  const words = ageWords(Math.abs(elapsed));
  const today = isToday(date);
  let age: string | null;
  if (Math.abs(elapsed) < 1) {
    age = "just now";
  } else if (words === null) {
    age = null;
  } else {
    age = elapsed > 0 ? `${words} ago` : `in ${words}`;
  }
  return {
    clock: today ? formatLocalDate(date, "HH:mm:ss") : formatLocalDate(date, "MMM d, HH:mm"),
    age,
    full: formatLocalDate(date, "EEE, MMM d yyyy 'at' HH:mm:ss"),
  };
}

/**
 * A timestamp as an age: "now", "12m", "3h", "2d", then the date once it is a week old. The full form carries the
 * absolute time for a tooltip. `now` is passed in so the table re-renders every minute from one clock.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  now: number,
): { short: string; full: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const minutes = Math.max(0, minutesBetween(now, date));
  return { short: shortAge(minutes, date), full: formatLocalDate(date, "EEE, MMM d yyyy 'at' HH:mm:ss") };
}

/** An age in whole `minutes` as "now", "12m", "3h", or "2d", and as `date`'s day once it is a week old. */
function shortAge(minutes: number, date: Date): string {
  const age = Duration.fromObject({ minutes });
  if (minutes < 1) return "now";
  if (age.toMillis() < HOUR.toMillis()) return `${minutes}m`;
  if (age.toMillis() < DAY.toMillis()) return `${Math.floor(age.as("hours"))}h`;
  if (age.toMillis() < WEEK.toMillis()) return `${Math.floor(age.as("days"))}d`;
  return formatLocalDate(date, "MMM d");
}
